import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type { SurveillanceEnvelope, SurveillanceService } from '@/lib/mqtt/schemas/surveillance';

export type SurvMode = 'fused' | 'diagnostic';

/** Live_Fused lifecycle (mockup `_setFusedStatus`). `starting` / `stopping` are
 *  the transient states the control panel shows while the request settles. */
export type FusedStatus = 'idle' | 'starting' | 'running' | 'stopping';

/**
 * Build the synthetic Live_Fused service from the inputs selected to fuse.
 *
 * Everything is derived from the inputs — no invented data. The fused feed's
 * receivers ARE its inputs' receivers, so `stations` and `coverageModels` are
 * the union of theirs, keeping the station list consistent with the sensor
 * count we report. Deduped by id in case two services share a receiver.
 */
function buildFusedService(
  byId: Record<string, SurveillanceService>,
  selected: readonly string[],
): SurveillanceService {
  const inputs = selected.map((id) => byId[id]).filter((s): s is SurveillanceService => s != null);
  const latencies = inputs
    .map((s) => s.avg_latency_ms)
    .filter((l): l is number => typeof l === 'number');
  // Union, not sum — one aircraft seen by both MLAT and ADS-B is one target.
  const targets = [...new Set(inputs.flatMap((s) => s.visibleTargets))];
  const stations = [
    ...new Map(inputs.flatMap((s) => s.stations).map((st) => [st.sensor_id, st])).values(),
  ];
  return {
    service_id: 'live_fused',
    label: 'Live_Fused',
    type: 'Cooperative',
    service_state: 'Active',
    trust: 'High',
    online_sensors: inputs.reduce((n, s) => n + s.online_sensors, 0),
    total_sensors: inputs.reduce((n, s) => n + s.total_sensors, 0),
    avg_latency_ms: latencies.length
      ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length)
      : null,
    stations,
    visibleTargets: targets,
    coverageModels: inputs.flatMap((s) => s.coverageModels),
  };
}

interface SurvStore {
  byId: Record<string, SurveillanceService>;
  mode: SurvMode;
  selectedServiceId: string | null;
  /** Diagnostic-mode network radio (mockup `setSurvNetwork`). */
  selectedNetwork: string | null;
  /** Index into `byId[selectedNetwork].coverageModels` — which AGL polygon
   * to draw on the map. Defaults to 0 (the first model) when a service is
   * selected, matching mockup `selectSurvNet` L10942. `null` = no polygon. */
  activeCovModelIdx: number | null;
  apply: (env: SurveillanceEnvelope) => void;
  setMode: (m: SurvMode) => void;
  selectService: (id: string | null) => void;
  setNetwork: (n: string | null) => void;
  setCovModel: (i: number | null) => void;
  /** Mockup `regenCovModel` (L10991): drop offline sensors from the model
   * and bump createdAt. Used by the Regenerate button on a Not-Accurate row. */
  regenCovModel: (serviceId: string, modelIdx: number) => void;

  /* ── Live_Fused (mockup "FUSED SURVEILLANCE ADMIN PANEL") ─────────────
   * Client-side state for now; the backend takes this over later. Nothing
   * here is persisted — a reload drops back to idle. */
  /** Service ids ticked in the fuse checklist. */
  fusedSelected: string[];
  fusedStatus: FusedStatus;
  /** Epoch ms the current run began — drives the duration readout. */
  fusedStartedAt: number | null;
  /**
   * The synthetic Live_Fused service while a fusion runs, held OUTSIDE `byId`.
   * `apply()` replaces `byId` wholesale on every surveillance envelope (~1 Hz),
   * so anything stored there would be wiped a second later. `apply()` instead
   * re-aggregates this from the fresh `byId`, keeping it live rather than a
   * start-time snapshot.
   */
  fusedService: SurveillanceService | null;
  toggleFusedService: (id: string) => void;
  /** Status → `starting`; the panel calls `confirmFusing` after the delay. */
  beginFusing: () => void;
  confirmFusing: () => void;
  /** Status → `stopping`; the panel calls `confirmStopFusing` after the delay. */
  beginStopFusing: () => void;
  confirmStopFusing: () => void;
  /** Restart keeps the same inputs and resets the duration clock. */
  restartFusing: () => void;
}

export const useSurvStore = create<SurvStore>()(
  devtools(
    subscribeWithSelector((set) => ({
      byId: {},
      mode: 'fused',
      selectedServiceId: null,
      selectedNetwork: null,
      activeCovModelIdx: null,
      fusedSelected: [],
      fusedStatus: 'idle',
      fusedStartedAt: null,
      fusedService: null,
      apply: (env) => set((s) => {
        const byId = Object.fromEntries(env.services.map((x) => [x.service_id, x]));
        // Re-aggregate on every envelope so Live_Fused tracks its inputs. A
        // snapshot taken at start would freeze at whatever the counts were
        // then — and sensor snapshots can lag the connect by up to a minute,
        // so a fusion started early would read 0 sensors forever.
        return s.fusedService
          ? { byId, fusedService: buildFusedService(byId, s.fusedSelected) }
          : { byId };
      }),
      // Switching to diagnostic clears any previous network so the UI
      // auto-selects the first one (matches mockup setSurvMode L9772).
      // Switching back to fused clears the network + cov model so the map
      // returns to the all-service overview.
      // Mockup `setSurvMode` L9770-9773: switching to Diagnostic auto-picks
      // the first network. Switching back to Fused clears it so the
      // all-service overlay is restored. First network per ticket display
      // order is `live_ads` (Live ADS).
      setMode: (m) => set({
        mode: m,
        selectedNetwork: m === 'diagnostic' ? 'live_ads' : null,
        activeCovModelIdx: m === 'diagnostic' ? 0 : null,
      }),
      selectService: (id) => set({ selectedServiceId: id }),
      // Auto-activating the first coverage model is right for a real service —
      // it owns its models. Live_Fused inherits a pile of them from different
      // inputs, so index 0 is an arbitrary one input's footprint: drawing it
      // implies it is the fusion's coverage, and the auto-fit then zooms to
      // that single circle instead of the whole fused picture. Start with none
      // active; the user can click a model to draw it.
      setNetwork: (n) =>
        set({
          selectedNetwork: n,
          activeCovModelIdx: n && n !== 'live_fused' ? 0 : null,
        }),
      setCovModel: (i) => set({ activeCovModelIdx: i }),
      regenCovModel: (serviceId, modelIdx) => set((state) => {
        const svc = state.byId[serviceId];
        if (!svc) return state;
        const model = svc.coverageModels[modelIdx];
        if (!model) return state;
        const onlineSet = new Set(svc.stations.filter((s) => s.state === 'live').map((s) => s.sensor_id));
        const trimmedSensors = model.sensors.filter((sid) => onlineSet.has(sid));
        if (trimmedSensors.length === model.sensors.length) return state;
        const now = new Date();
        const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
        const ts = `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())} ` +
          `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())} UTC`;
        const nextModels = svc.coverageModels.map((m, i) =>
          i === modelIdx ? { ...m, sensors: trimmedSensors, createdAt: ts } : m,
        );
        return {
          byId: {
            ...state.byId,
            [serviceId]: { ...svc, coverageModels: nextModels },
          },
        };
      }),

      toggleFusedService: (id) =>
        set((s) => ({
          fusedSelected: s.fusedSelected.includes(id)
            ? s.fusedSelected.filter((x) => x !== id)
            : [...s.fusedSelected, id],
        })),
      // Guarded here as well as on the button: fusing one stream with nothing
      // is not a fusion.
      beginFusing: () =>
        set((s) => (s.fusedSelected.length < 2 ? s : { fusedStatus: 'starting' as const })),
      confirmFusing: () =>
        set((s) => ({
          fusedStatus: 'running',
          fusedStartedAt: Date.now(),
          fusedService: buildFusedService(s.byId, s.fusedSelected),
        })),
      beginStopFusing: () => set({ fusedStatus: 'stopping' }),
      // Stopping un-configures Live_Fused: it drops out of the service list and
      // the topbar chips until started again (mockup `stopFusedSurv`).
      confirmStopFusing: () =>
        set((s) => ({
          fusedStatus: 'idle',
          fusedStartedAt: null,
          fusedService: null,
          // Leaving it selected would strand the detail pane on a service that
          // no longer exists.
          ...(s.selectedNetwork === 'live_fused'
            ? { selectedNetwork: null, activeCovModelIdx: null, mode: 'fused' as const }
            : {}),
        })),
      restartFusing: () => set({ fusedStartedAt: Date.now() }),
    })),
    { name: 'surv' },
  ),
);

/** Broker services, plus Live_Fused while a fusion runs. `fusedService` keeps a
 *  stable identity between renders, so `useShallow` still short-circuits. */
export const selectSurvServices = (s: SurvStore): SurveillanceService[] =>
  s.fusedService ? [...Object.values(s.byId), s.fusedService] : Object.values(s.byId);

/** Services that can be fused — everything the broker actually reports.
 *  Live_Fused is never an input to itself. */
export const selectFusableServices = (s: SurvStore): SurveillanceService[] =>
  Object.values(s.byId);

/** Resolve a service by id. Must be used instead of reading `byId` directly:
 *  Live_Fused lives outside that map, so a bare `byId[id]` lookup renders
 *  "Service not found" for it. */
export const selectServiceById =
  (id: string) =>
  (s: SurvStore): SurveillanceService | undefined =>
    s.byId[id] ?? (s.fusedService?.service_id === id ? s.fusedService : undefined);
